/**
 * BuilderStepTrack — the homepage's palette-dash progress marker, applied to
 * the one-step campaign builder: one segment per section, filled in that
 * section's accent once its required fields are in. Optional sections fill
 * when the student adds something but never count against "ready".
 * Each segment scrolls to its section.
 */
const STEPS = [
  { key: "personal", label: "Personal details", accent: "bg-cobalt", anchor: "builder-personal", done: (c) => c.full_name?.done && c.school?.done },
  { key: "profile", label: "Profile photo & bio", accent: "bg-blossom", anchor: "builder-profile", done: (c) => c.bio?.done },
  {
    key: "funding", label: "Funding need", accent: "bg-yellow", anchor: "builder-funding",
    done: (c, f) => c.title?.done && c.story?.done && c.category?.done && Number(f.goal) >= 1 && (!c.request_doc?.required || c.request_doc?.done),
  },
  { key: "photos", label: "Photos (optional)", accent: "bg-tangerine", anchor: "builder-photos", optional: true, done: (c) => c.photos?.done },
  { key: "video", label: "Video pitch (optional)", accent: "bg-scarlet", anchor: "builder-video", optional: true, done: (c) => c.video?.done },
  { key: "visibility", label: "Profile visibility", accent: "bg-grass", anchor: "builder-visibility", done: (_, f) => Boolean(f.visibility) },
  { key: "attestations", label: "Attestations", accent: "bg-ink", anchor: "attestations-group", done: (c) => c.attestations?.done },
];

const scrollTo = (anchor) => document.querySelector(`[data-testid="${anchor}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" });

export const BuilderStepTrack = ({ checklist, form }) => {
  if (!checklist || !form) return null;
  const items = checklist.items || {};
  const state = STEPS.map((s) => ({ ...s, done: Boolean(s.done(items, form)) }));
  const required = state.filter((s) => !s.optional);
  const doneCount = required.filter((s) => s.done).length;
  const ready = doneCount === required.length;

  return (
    <div className="mt-5" data-testid="builder-step-track">
      <div className="flex flex-wrap items-center gap-1.5">
        {state.map((s) => (
          <button
            key={s.key}
            type="button"
            onClick={() => scrollTo(s.anchor)}
            title={s.label}
            aria-label={`${s.label}: ${s.done ? "done" : "not yet"}`}
            data-testid={`builder-step-${s.key}`}
            data-done={s.done}
            className={`h-2 w-7 rounded-full transition-colors hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/40 focus-visible:ring-offset-2 ${s.done ? s.accent : "bg-ink/10"}`}
          />
        ))}
      </div>
      <p className="mt-2 text-[13px] text-olive" data-testid="builder-step-caption">
        {ready ? "Every required section is done. Review, then submit." : `${doneCount} of ${required.length} required sections done. Photos and video are optional.`}
      </p>
    </div>
  );
};
